import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Text, Button, VStack, HStack, Input } from '@chakra-ui/react';
import PhotoUploadSection from '../components/PhotoUploadSection';
import BackButton from '../components/BackButton';
import LocationSelector from '../components/LocationSelector';
import { MAX_PHOTO_SIZE_BYTES, MIN_PHOTOS_REQUIRED, ROUTES, USER_TYPES } from '../constants';
import { useConfig } from '../contexts/ConfigContext';
import { useUser } from '../contexts/UserContext';
import { getCachedUserProfile } from '../utils';

const InfluencerEditProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { config } = useConfig();
  const { user } = useUser();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    country: '',
    city: '',
    instagram: '',
    tiktok: '',
    youtube: ''
  });
  const [uploadedPhotos, setUploadedPhotos] = useState<File[]>([]);
  const [photoUrls, setPhotoUrls] = useState<string[]>([]);

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDialogVisible, setIsDialogVisible] = useState(false);

  // Fetch profile data on mount
  useEffect(() => {
    const fetchProfile = async () => {
      if (!config?.apiUrl || !user?.id) {
        return;
      }

      try {
        const profileData = await getCachedUserProfile(config.apiUrl, user.id, user.userType);

        // Extract data from the profile response
        setFormData({
          firstName: profileData.data?.firstName || '',
          lastName: profileData.data?.lastName || '',
          country: profileData.country || '',
          city: profileData.city || '',
          instagram: profileData.data?.social?.instagram || '',
          tiktok: profileData.data?.social?.tiktok || '',
          youtube: profileData.data?.social?.youtube || ''
        });

        // Set photo URLs if available
        if (profileData.data?.photos && Array.isArray(profileData.data.photos)) {
          setPhotoUrls(profileData.data.photos);
        }
      } catch (error) {
        console.error('Failed to fetch profile:', error);
      }
    };

    fetchProfile();
  }, [config, user]);

  useEffect(() => {
    if (isDeleteDialogOpen) {
      setIsDialogVisible(true);
    } else {
      setIsDialogVisible(false);
    }
  }, [isDeleteDialogOpen]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDeleteAccount = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleCancelDelete = () => {
    setIsDeleteDialogOpen(false);
  };

  const handleConfirmDelete = () => {
    // Handle delete account logic here
    console.log('Account deleted');
    setIsDeleteDialogOpen(false);
    // Navigate to landing page or login
    navigate(ROUTES.HOME);
  };

  const handlePageClick = (e: React.MouseEvent) => {
    // Close dialog if clicking anywhere outside the dialog
    if (isDeleteDialogOpen) {
      setIsDeleteDialogOpen(false);
    }
  };

  return (
    <Box
      className="base-container-long"
      onClick={handlePageClick}
    >
      {/* Navigation Bar */}
      <Box className='edit-profiles-top' >
        {/* Back Button */}

        <BackButton onClick={handleBack} />

        {/* Title */}
        <Text
          className="subtitle-text"
          marginLeft="24px"
        >
          Edit profile
        </Text>
      </Box>

      {/* Form Content */}
      <Box className='relative-box' >
        {/* Name Section */}
        <Box className='text-relative-box' >
          <VStack spacing="20px" alignItems="stretch">
            {/* First Name */}
            <Box>
              <Text
                className='static-text-white'
                marginBottom="14px"
              >
                First Name
              </Text>
              <Input
                className='input-in-form'
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
              />
            </Box>

            {/* Last Name */}
            <Box>
              <Text
                className='static-text-white'
                marginBottom="14px"
              >
                Last Name
              </Text>
              <Input
              className='input-in-form'
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
              />
            </Box>
          </VStack>
        </Box>
        {/* Country Section */}
        <Box className='text-relative-box' >

          <LocationSelector
            selectedCountry={formData.country}
            selectedCity={formData.city}
            onCountryChange={(country) => handleInputChange('country', country)}
            onCityChange={(city) => handleInputChange('city', city)}
            showCountryTitle={true}
            showCityTitle={true}
          />
        </Box>

        {/* Instagram Section */}
        <Box className='text-relative-box' >
          <Text
            className='static-text-white'
            marginBottom="16px"
          >
            Instagram*
          </Text>
          <Input
            className='input-in-form'
            value={formData.instagram}
            onChange={(e) => handleInputChange('instagram', e.target.value)}
          />
        </Box>

        {/* TikTok Section */}
        <Box className='text-relative-box' >
          <Text
            className='static-text-white'
            marginBottom="16px"
          >
            Tiktok
          </Text>
          <Input
            className='input-in-form'
            value={formData.tiktok}
            onChange={(e) => handleInputChange('tiktok', e.target.value)}
          />
        </Box>

        {/* YouTube Section */}
        <Box className='text-relative-box' >
          <Text
            className='static-text-white'
            marginBottom="16px"
          >
            Youtube
          </Text>
          <Input
            className='input-in-form'
            value={formData.youtube}
            onChange={(e) => handleInputChange('youtube', e.target.value)}
          />
        </Box>

        {/* Photos Section */}
        <PhotoUploadSection
          uploadedPhotos={uploadedPhotos}
          setUploadedPhotos={setUploadedPhotos}
          photoUrls={photoUrls}
          setPhotoUrls={setPhotoUrls}
          maxPhotoSize={MAX_PHOTO_SIZE_BYTES}
          title="Make an impression with your photos, upload your best shots!"
          emptyStateTitle="Upload photos"
          emptyStateSubtitle={`Upload at least ${MIN_PHOTOS_REQUIRED} photos`}
          buttonText="Add more photos"
          showTips={true}
          tipsContext={USER_TYPES.INFLUENCER}
        />

        {/* Danger Zone */}
        <Box
          className='text-relative-box'
          marginTop="40px"
        >
          <Text
            className='subtitle-text'
            color="red"
            marginBottom="16px"
          >
            Danger zone
          </Text>

          <HStack
            alignItems="center"
            cursor="pointer"
            spacing="8px"
            onClick={handleDeleteAccount}
          >
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14.0241 20.961C13.2818 20.961 12.5353 21.0078 11.7983 20.9509C10.3516 20.8394 9.19389 19.7996 9.02235 18.4856C8.88722 17.4518 8.80048 16.4129 8.68979 15.3765C8.58102 14.362 8.46697 13.348 8.35915 12.3331C8.30357 11.8109 8.25086 11.2884 8.20965 10.7654C8.19335 10.5608 8.1186 10.4589 7.87374 10.4842C7.75011 10.4974 7.61929 10.4716 7.49374 10.4528C7.19377 10.4095 7.01216 10.2201 7.00066 9.95032C6.9882 9.65035 7.15208 9.43084 7.45253 9.36568C7.56754 9.34076 7.68973 9.33507 7.80857 9.33464C8.54316 9.33201 9.27823 9.33157 10.0128 9.33464C10.1561 9.33551 10.2395 9.31059 10.286 9.16147C10.6808 7.89599 11.8174 7.05816 13.2564 7.014C13.9393 6.99301 14.6408 6.97989 15.3064 7.0949C16.5293 7.30566 17.3118 8.02805 17.6817 9.10944C17.7406 9.28129 17.8188 9.33945 18.0123 9.33726C18.7627 9.32808 19.5136 9.3307 20.2645 9.33464C20.7638 9.33726 21.0279 9.57426 20.9977 9.97831C20.9804 10.2101 20.7993 10.4073 20.5444 10.4471C20.3723 10.4737 20.1955 10.486 20.0211 10.4864C19.874 10.4873 19.8021 10.5319 19.7887 10.6714C19.7154 11.4331 19.6382 12.1949 19.5592 12.9562C19.4528 13.9781 19.3421 15 19.2352 16.0219C19.1509 16.8265 19.0886 17.6337 18.9827 18.4357C18.8059 19.772 17.6707 20.8193 16.2034 20.9689C15.6116 21.0292 15.0069 20.9829 14.4079 20.985C14.28 20.985 14.1525 20.985 14.0246 20.985C14.0246 20.9767 14.0246 20.9689 14.0246 20.9606L14.0241 20.961ZM13.9771 19.8153C13.9771 19.821 13.9771 19.8267 13.9771 19.8324C14.5521 19.8324 15.1281 19.8451 15.7022 19.8271C15.9849 19.8184 16.2863 19.7996 16.5436 19.7051C17.3884 19.3942 17.7196 18.758 17.789 17.9656C17.8815 16.9131 17.9865 15.861 18.0962 14.8098C18.2385 13.4468 18.3895 12.0842 18.5409 10.7217C18.5596 10.5533 18.5083 10.4864 18.3109 10.4864C15.4271 10.4908 12.5434 10.4908 9.65966 10.4864C9.46847 10.4864 9.42342 10.5538 9.44451 10.7086C9.47949 10.9613 9.5092 11.2149 9.53555 11.469C9.61174 12.2014 9.6841 12.9339 9.76029 13.6663C9.84511 14.4858 9.92801 15.3052 10.0195 16.1238C10.1077 16.9131 10.1877 17.7041 10.3061 18.4899C10.4072 19.1607 11.1787 19.7886 11.9161 19.811C12.6023 19.8319 13.2895 19.8153 13.9762 19.8153H13.9771ZM16.4191 9.31321C16.2446 8.73163 15.5848 8.2187 14.9043 8.18109C14.3011 8.14786 13.6925 8.14874 13.0892 8.18109C12.4068 8.21783 11.7408 8.73644 11.5793 9.31321H16.4195H16.4191Z" fill="#F00C23"/>
              <path d="M11 17.2143C11.0955 17.0544 11.1752 16.8486 11.3145 16.7007C11.784 16.2001 12.2693 15.7137 12.7612 15.2358C12.9393 15.0628 12.9331 14.9478 12.7573 14.7771C12.2648 14.2992 11.7863 13.8071 11.3061 13.3167C10.9674 12.9709 10.9523 12.5287 11.2606 12.2107C11.5481 11.9136 12.0025 11.9306 12.335 12.2623C12.8342 12.76 13.3313 13.2606 13.8199 13.7686C13.9642 13.9188 14.0659 13.9211 14.2113 13.7708C14.7145 13.2504 15.2256 12.7373 15.7383 12.2265C15.9742 11.9913 16.3202 11.9369 16.5909 12.0746C16.8588 12.2112 17.0514 12.5276 16.9806 12.8229C16.9346 13.0134 16.8228 13.209 16.6886 13.3524C16.2264 13.8468 15.7462 14.3247 15.2627 14.7981C15.106 14.9517 15.1043 15.0606 15.2604 15.2148C15.7687 15.7165 16.2736 16.2228 16.7672 16.7392C17.1272 17.1157 17.0598 17.6264 16.633 17.9003C16.3578 18.0766 15.9709 18.0171 15.6889 17.7381C15.2014 17.2551 14.7134 16.7721 14.2416 16.2732C14.0664 16.0878 13.9608 16.1031 13.7924 16.2794C13.3122 16.7829 12.8213 17.2767 12.322 17.7608C11.948 18.1236 11.3724 18.0613 11.1415 17.6435C11.0803 17.5323 11.0595 17.3985 11 17.2137V17.2143Z" fill="#F00C23"/>
            </svg>
            <Text
              fontFamily="Urbanist, sans-serif"
              fontWeight="700"
              fontSize="16px"
              lineHeight="1em"
              color="#F00C23"
            >
              Delete Account
            </Text>
          </HStack>
        </Box>
      </Box>

      {/* Save Button */}
      <Box
        className='form-container'
        bottom="30px"
      >
        <Button className='button-big' >
          Save
        </Button>
      </Box>

      {/* Modal Backdrop */}
      {isDeleteDialogOpen && (
        <Box className="modal-backdrop" zIndex={1} onClick={handlePageClick} />
      )}

      {/* Delete Account Dialog */}
      {isDeleteDialogOpen && (
        <Box
          className="bottom-sheet-dialog"
          height="490px"
          zIndex={3}
          transform={isDialogVisible ? "translateY(0)" : "translateY(100%)"}
          transition="transform 0.3s ease-in-out"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Background Image */}
          <Box
            className="bottom-sheet-bg"
            style={{ backgroundImage: "url('/assets/images/confirmation-sheet-bg.svg')" }}
          />

          {/* Bottom Sheet Indicator */}
          <Box
            className="bottom-sheet-indicator"
            style={{ backgroundImage: "url('/assets/images/bottom-sheet-indicator.svg')" }}
          />

          {/* Content */}
          <VStack className="bottom-sheet-content" paddingTop="44px">
            {/* Main Question */}
            <Text
              className="static-text-white"
              marginBottom="100px"
              textAlign="center"
            >
              Are you sure you want to delete your account? This action cannot be undone.
            </Text>

            {/* Cancel Button */}
            <Button
              className="button-cancel"
              onClick={handleCancelDelete}
              bottom="30px"
            >
              Cancel
            </Button>

            {/* Delete Button */}
            <Button
              className="button-delete"
              onClick={handleConfirmDelete}
              bottom="30px"
              >

              Delete Account
            </Button>
          </VStack>

          <Box
            className="bottom-sheet-footer"
            style={{ backgroundImage: "url('/assets/images/home-indicator-confirmation.svg')" }}
          />
        </Box>
      )}



    </Box>
  );
};

export default InfluencerEditProfilePage;
